/**
 * The form of one alert channel (the Alert Channels page). A channel's kind is an
 * alerter plugin, and the form's settings are generated from that plugin's configuration
 * type: its tree, as the registration carries it (type-tree-form.ts: a record's fields, an
 * enum as a choice that opens the chosen case's fields, a Duration as a number and a unit, a
 * secret type as a choice among the stored secrets of that type). A registration from
 * before the tree carries only flat fields, read the old way:
 *
 *   Str            a text box
 *   Num            a number
 *   Bool           a switch
 *   [Str]          a text box, values separated by commas
 *   a secret type  a choice among the stored secrets of that type (e.g. SlackWebhook)
 *
 * A secret field holds the NAME of a stored secret; the value is on the Secrets page and
 * never here. A channel whose settings name every secret its plugin needs is sent by the
 * plugin.
 */
import type { FormItem } from "./form-builder.model";
import type { FormAction } from "./renderer/form-renderer";
import { datasetFormRequest } from "./dataset-form-signal";
import { notify } from "../notifications/NotificationProvider";
import { ViewManager } from "../../services/view-manager";
import { newUid } from "./monitor-provision";
import {
  listAlerterPlugins,
  listChannels,
  saveChannel,
  type AlertChannel,
  type AlerterPlugin,
  type PluginField,
} from "../../services/alert-channel-rpc-client";
import { listSecrets, listSecretTypes, type Secret, type SecretType } from "../../services/secret-admin-rpc-client";
import { itemsFor, labelOf as treeLabelOf, topLevelFields } from "./type-tree-form";
import { planOf, type LoadedPlan } from "../../checkpoint-vm/plans";
import { defaultsNote, readTypeTree } from "./type-tree";

export type AlertChannelFormOpts = {
  /** alertchannel uid to edit; undefined = new */
  key?: string;
  /** a view to re-fetch after a save (the page's list) */
  refreshView?: string;
  /** for a new channel: the kind to show the settings of, and the name typed so far */
  kind?: string;
  name?: string;
};

const settingName = (field: string): string => `setting.${field}`;

/** "authToken" as a label: "Auth token". */
export const labelOf = treeLabelOf;

/** The stored-secret type a configuration field holds, when its type is a secret type. */
export function secretTypeOf(field: PluginField, types: SecretType[]): SecretType | undefined {
  return types.find((t) => t.checkpointType !== "" && t.checkpointType === field.type);
}

/** A field's form item. `secrets` are the stored secrets, to choose among for a secret field. */
export function itemFor(field: PluginField, types: SecretType[], secrets: Secret[]): FormItem {
  const base = { name: settingName(field.name), label: labelOf(field.name), required: !field.hasDefault };
  const optional = field.hasDefault ? " Blank takes the plugin's default." : "";
  const secretType = secretTypeOf(field, types);
  if (secretType) {
    const choices = secrets.filter((s) => s.kind === secretType.kind);
    return {
      ...base,
      type: "select",
      options: [
        ...(field.hasDefault ? [{ value: "", text: "(none)" }] : []),
        ...choices.map((s) => ({ value: s.name, text: `${s.name} — ${s.description}` })),
      ],
      helpText:
        choices.length === 0
          ? `No ${secretType.kind} secret is stored yet. Add one on the Secrets page, then choose it here.`
          : `A stored ${secretType.kind} secret, by name. Its value is on the Secrets page and is never shown here.`,
    };
  }
  switch (field.type) {
    case "Num":
      return { ...base, type: "number", helpText: optional.trim() };
    case "Bool":
      return { ...base, type: "toggle", required: false };
    case "[Str]":
      return { ...base, type: "text", helpText: `Several values, separated by commas.${optional}` };
    default:
      return { ...base, type: "text", helpText: optional.trim() };
  }
}

/** What a stored setting shows as in its form item. */
export function shownValue(field: PluginField, stored: unknown): unknown {
  if (field.type === "Bool") return Boolean(stored);
  if (field.type === "[Str]") return Array.isArray(stored) ? stored.join(", ") : "";
  return stored === undefined || stored === null ? "" : stored;
}

/**
 * The channel's settings after the form: each of the plugin's fields as typed, a field left blank
 * left out (the plugin's default applies), and every setting that is not one of the plugin's fields
 * kept as stored.
 */
export function settingsFrom(
  plugin: AlerterPlugin,
  values: Record<string, unknown>,
  stored: Record<string, unknown>,
): Record<string, unknown> {
  const settings: Record<string, unknown> = { ...stored };
  for (const field of plugin.fields) {
    const typed = values[settingName(field.name)];
    delete settings[field.name];
    if (field.type === "Bool") {
      settings[field.name] = Boolean(typed);
    } else if (field.type === "Num") {
      if (typed !== "" && typed !== undefined && typed !== null) settings[field.name] = Number(typed);
    } else if (field.type === "[Str]") {
      const list = String(typed ?? "")
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
      if (list.length > 0) settings[field.name] = list;
    } else {
      const text = String(typed ?? "").trim();
      if (text !== "") settings[field.name] = text;
    }
  }
  return settings;
}

/**
 * The channel's settings after a form generated from the configuration type's tree: the type's
 * fields as the checkpoint vm assembles them from the form, and every setting that is not one of them
 * kept as stored. Throws a CheckpointVmError at a field whose value is not of its type.
 */
export function settingsFromTree(
  plugin: AlerterPlugin & { config: NonNullable<AlerterPlugin["config"]> },
  loaded: LoadedPlan,
  values: Record<string, unknown>,
  stored: Record<string, unknown>,
): Record<string, unknown> {
  const settings: Record<string, unknown> = { ...stored };
  for (const name of topLevelFields(plugin.config)) delete settings[name];
  const assembled = loaded.vm.fromForm(loaded.handle, plugin.name, values, "setting");
  return { ...settings, ...(assembled && typeof assembled === "object" ? (assembled as Record<string, unknown>) : {}) };
}

export async function openAlertChannelForm(opts: AlertChannelFormOpts): Promise<void> {
  let plugins: AlerterPlugin[];
  let types: SecretType[];
  let secrets: Secret[];
  let existing: AlertChannel | undefined;
  try {
    [plugins, types, secrets] = await Promise.all([listAlerterPlugins(), listSecretTypes(), listSecrets()]);
    if (opts.key) existing = (await listChannels()).find((c) => c.uid === opts.key);
  } catch (e) {
    notify({ severity: "error", title: "Could not load the channel", message: String(e) });
    return;
  }
  if (opts.key && !existing) {
    notify({ severity: "error", title: "Not found", message: `No alert channel ${opts.key}.` });
    return;
  }
  const kind = existing?.kind ?? opts.kind ?? plugins[0]?.name ?? "";
  const plugin = plugins.find((p) => p.name === kind);
  if (!plugin) {
    notify({
      severity: "error",
      title: "No plugin for this kind",
      message: kind
        ? `No alerter plugin named ${kind} is registered; a push of its fileset registers it.`
        : "No alerter plugin is registered; a push of a fileset that declares one registers it.",
    });
    return;
  }
  const stored = existing?.target ?? {};
  // the plugin's plan in the tab's checkpoint vm, which reads and writes its settings
  let loaded: LoadedPlan | undefined;
  if (plugin.config) {
    try {
      loaded = await planOf(plugin.filesetUid);
      // the form is built from the same plan its settings are read and written by
      plugin.config = readTypeTree(loaded.vm.typeTree(loaded.handle, plugin.name));
    } catch (e) {
      notify({ severity: "error", title: "Could not load the channel", message: `The ${plugin.name} plugin's program did not load: ${String(e)}` });
      return;
    }
  }

  const items: FormItem[] = [
    { name: "name", label: "Name", type: "text", required: true, helpText: "What a monitor's form lists this channel as." },
    {
      name: "kind",
      label: "Kind",
      type: "select",
      required: true,
      disabled: Boolean(existing),
      options: plugins.map((p) => ({ value: p.name, text: p.name })),
      helpText: existing
        ? `Sent by the ${plugin.name} alerter plugin (fileset ${plugin.fileset}).`
        : `The alerter plugin that sends this channel. The settings below are the ${plugin.name} plugin's; choose another kind and press Show its settings.`,
    },
    { name: "enabled", label: "Enabled", type: "toggle" },
    ...(plugin.config
      ? itemsFor(plugin.config, "setting", "", { types, secrets }, { required: true, hasDefault: false })
      : plugin.fields.map((f) => itemFor(f, types, secrets))),
  ];

  const initialValues: Record<string, unknown> = {
    name: existing?.name ?? opts.name ?? "",
    kind,
    enabled: existing?.enabled ?? true,
  };
  if (plugin.config && loaded) Object.assign(initialValues, loaded.vm.toForm(loaded.handle, plugin.name, loaded.vm.defaults(loaded.handle, plugin.name, stored), "setting"));
  else for (const f of plugin.fields) initialValues[settingName(f.name)] = shownValue(f, stored[f.name]);

  // A new channel's fields are its kind's: choosing another kind shows that kind's settings.
  const showKind: FormAction = {
    label: "Show its settings",
    run: async (values) => {
      await openAlertChannelForm({
        refreshView: opts.refreshView,
        kind: String(values["kind"] ?? ""),
        name: String(values["name"] ?? ""),
      });
    },
  };

  datasetFormRequest.next({
    title: existing ? existing.name : "New alert channel",
    sections: [{ title: "", items, note: defaultsNote(plugin.config) }],
    initialValues,
    submitLabel: "Save",
    actions: existing || plugins.length < 2 ? [] : [showKind],
    onCancel: () => datasetFormRequest.next(undefined),
    onSubmit: async (values) => {
      if (!existing && String(values["kind"] ?? "") !== plugin.name) {
        notify({
          severity: "warning",
          title: "Not saved",
          message: `The settings shown are the ${plugin.name} plugin's. Press Show its settings for ${values["kind"]}.`,
        });
        return;
      }
      let target: Record<string, unknown>;
      try {
        target = plugin.config && loaded ? settingsFromTree({ ...plugin, config: plugin.config }, loaded, values, stored) : settingsFrom(plugin, values, stored);
      } catch (e) {
        notify({ severity: "error", title: "Not saved", message: `The settings are not valid: ${String(e instanceof Error ? e.message : e)}` });
        return;
      }
      const channel: AlertChannel = {
        uid: existing?.uid ?? newUid(),
        name: String(values["name"] ?? "").trim(),
        kind: plugin.name,
        enabled: Boolean(values["enabled"]),
        target,
      };
      const res = await saveChannel(channel, !existing);
      if (!res.success) {
        notify({ severity: "error", title: "Not saved", message: res.error || "save failed" });
        return;
      }
      notify({ severity: "success", title: "Saved", message: `Saved ${channel.name}` });
      if (opts.refreshView) ViewManager.get().refreshView(opts.refreshView);
      await openAlertChannelForm({ key: channel.uid, refreshView: opts.refreshView });
    },
  });
}
